import { Box, Chip, Stack, Typography } from "@mui/material";
import { FieldList } from "@/components/Responsive";

export type V2Record = Record<string, unknown>;
export const asRecord = (value: unknown): V2Record =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as V2Record)
    : {};
export const asList = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];
export const hasValue = (value: unknown): boolean =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  (Array.isArray(value)
    ? value.length > 0
    : typeof value !== "object" || Object.keys(asRecord(value)).length > 0);

// An explicit allowlist applies at every depth: schema additions must never
// expose provenance, JSON pointers, audit or unresolved parser data by default.
const publicFields = new Set([
  "不動産番号",
  "名称",
  "建物番号",
  "所在家屋番号",
  "土地",
  "建物",
  "区分建物",
  "所在",
  "地番",
  "地目",
  "地積",
  "現在の所在",
  "現在の地番",
  "現在の地目",
  "現在の地積",
  "現在の地目地積",
  "家屋番号",
  "建物の名称",
  "現在の家屋番号",
  "現在の建物の名称",
  "現在の種類",
  "現在の構造",
  "現在の各階床面積",
  "現在の床面積",
  "延床面積",
  "新築年月日",
  "主たる建物",
  "附属建物",
  "専有部分",
  "一棟の建物",
  "敷地権の目的である土地",
  "敷地権",
  "所在及び地番",
  "種類",
  "構造",
  "各階床面積",
  "床面積",
  "床面積合計",
  "階",
  "符号",
  "割合",
  "敷地権の種類",
  "敷地権の割合",
  "土地表示",
  "明細",
  "状態",
  "現在状態",
  "所有者",
  "所有者住所",
  "氏名",
  "住所",
  "持分",
  "ラベル",
  "役割",
  "順位番号",
  "主順位",
  "登記の目的",
  "目的",
  "受付",
  "受付日",
  "受付番号",
  "原因",
  "登記日付",
  "権利者",
  "債権額",
  "極度額",
  "元本極度額",
  "債務者",
  "連帯債務者",
  "指定債務者",
  "担保権者",
  "現在の担保権者",
  "現在の債務者",
  "現在の債権額極度額",
  "共同担保物件",
  "共同担保",
  "取扱店",
  "現在の取扱店",
  "利息",
  "利率",
  "損害金",
  "債権の範囲",
  "弁済期",
  "確定期日",
  "用益権",
  "記号",
  "番号",
  "調製",
  "対象権利",
  "表示原文",
  "種別",
  "存否",
]);
export const erased = (value: unknown): boolean => {
  const record = asRecord(value);
  return (
    record.抹消推定 === true ||
    record.抹消済み === true ||
    record.全部抹消済み === true
  );
};

export const Fields = ({
  value,
  showErased,
}: {
  value: unknown;
  showErased: boolean;
}): JSX.Element => {
  if (!hasValue(value))
    return (
      <Typography color="text.secondary" variant="body2">
        情報なし
      </Typography>
    );
  if (Array.isArray(value)) {
    const visible = asList(value).filter((item) => showErased || !erased(item));
    if (!visible.length)
      return (
        <Typography color="text.secondary" variant="body2">
          表示する情報はありません。
        </Typography>
      );
    return (
      <Stack spacing={1}>
        {visible.map((item, index) => (
          <Box
            key={index}
            sx={{
              pl: typeof item === "object" ? 1.5 : 0,
              borderLeft: typeof item === "object" ? 2 : 0,
              borderColor: "divider",
              color: erased(item) ? "text.secondary" : "text.primary",
            }}
          >
            {erased(item) && (
              <Chip
                size="small"
                variant="outlined"
                label="抹消済み・抹消推定"
                sx={{ mb: 1 }}
              />
            )}
            <Fields value={item} showErased={showErased} />
          </Box>
        ))}
      </Stack>
    );
  }
  if (typeof value === "object") {
    const record = asRecord(value);
    // Rank, purpose, structure, amount and cause have a user-facing source text.
    if (typeof record.原文 === "string" && record.原文)
      return <Fields value={record.原文} showErased={showErased} />;
    // Current trust party attributes are wrapped in a certainty result.
    if ("値" in record)
      return (
        <Stack spacing={0.5}>
          <Fields value={record.値} showErased={showErased} />
          {typeof record.状態 === "string" && (
            <Typography variant="caption">状態: {record.状態}</Typography>
          )}
          {asList(record.未解決).map((reason, index) => (
            <Typography key={index} variant="caption">
              {String(reason)}
            </Typography>
          ))}
        </Stack>
      );
    const items = Object.entries(record).filter(
      ([key, item]) =>
        publicFields.has(key) &&
        hasValue(item) &&
        // Joint-security entries already show their parsed location and lot.
        !(
          (key === "表示原文" || key === "対象権利") &&
          (hasValue(record.所在) ||
            hasValue(record.地番) ||
            hasValue(record.家屋番号))
        )
    );
    if (!items.length)
      return (
        <Typography color="text.secondary" variant="body2">
          表示する情報はありません。
        </Typography>
      );
    return (
      <Stack spacing={1.5}>
        {items.map(([key, item]) => {
          const content = (
            <Fields
              value={
                typeof item === "number" && /地積|床面積/.test(key)
                  ? `${item.toLocaleString("ja-JP")} ㎡`
                  : typeof item === "number" && key.includes("額")
                  ? `${item.toLocaleString("ja-JP")} 円`
                  : item
              }
              showErased={showErased}
            />
          );
          const nested =
            typeof item === "object" &&
            !asRecord(item).原文 &&
            !("値" in asRecord(item));
          return nested ? (
            <Box key={key}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {key}
              </Typography>
              {content}
            </Box>
          ) : (
            <FieldList
              key={key}
              items={[
                [
                  key === "ラベル"
                    ? "役割"
                    : key === "目的"
                    ? "登記の目的"
                    : key,
                  content,
                ],
              ]}
            />
          );
        })}
      </Stack>
    );
  }
  return (
    <Typography
      component="span"
      variant="body2"
      sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
    >
      {typeof value === "boolean" ? (value ? "あり" : "なし") : String(value)}
    </Typography>
  );
};
